<script>
  // @ts-check
  import { createEventDispatcher, onMount } from "svelte";
  import { cubicInOut } from "svelte/easing";
  import { fade } from "svelte/transition";

  import Button from "../../atoms/Button.svelte";
  import Icon from "../../atoms/Icon.svelte";

  /** Primarily used for testing
   * @type {string | undefined} */
  export let id = undefined;

  /** The label for the checkbox
   * @type {string} */
  export let label;

  /** The `autocomplete` prop determines whether the browser should automatically complete the input value.
   * @type {string | undefined} */
  export let autocomplete = undefined;

  /** Indicates whether the input field is required or not.
   * @type {boolean} */
  export let required = false;

  /** Indicates whether the input field is required or not.
   * @type {string} */
  export let selectedValue = null;

  /** Field should be focused on mount
   * @type {boolean} */
  export let focusOnMount = false;

  /**
   * An optional CSS class to be applied to the component.
   * @type {string}
   */
  let componentClass = "";
  export { componentClass as class };

  let inputField;
  let inputFieldDummy;
  let isFocused = false;
  let isClicked = true;
  let hasValue = false;

  const dispatch = createEventDispatcher();

  const handleInputChange = (event) => {
    const inputLength = event.target.value.length;
    hasValue = true;

    // Determine input width using a fake element
    if (inputFieldDummy) {
      inputFieldDummy.textContent = event.target.value;
      inputField.style.width = `${inputFieldDummy.offsetWidth}px`;
    }

    if (inputLength <= 0) {
      hasValue = false;
    }
  };

  const handleClearButtonClick = () => {
    dispatch("clear", inputField.value);
    inputField.focus();
    inputField.value = "";
    hasValue = false;
    inputField.style.width = "100%";
  };

  // Calculate input width on mount and animate label
  onMount(() => {
    setTimeout(() => {
      if (selectedValue) {
        handleInputChange({ target: { value: selectedValue } });
      }
      focusOnMount && inputField?.focus({ preventScroll: true });
    }, 100);
  });

  // Calculate input width when selectedValue changes
  $: {
    if (selectedValue) {
      handleInputChange({ target: { value: selectedValue } });
    }
  }

  const transitionClass = "transition-all input-nav-easing";

  $: containerFocusClass =
    isFocused && !isClicked ? "outline-2 outline-dashed outline-offset-[6px] outline-black" : "";
  $: labelOpacityClass = hasValue ? "opacity-[0]" : "delay-[300ms] opacity-[1]";
  $: animatedLabelClass = hasValue
    ? "opacity-[1] top-[-20px] md:top-[-22px] xl:top-[-27px]"
    : "opacity-[0] top-[-10px] md:top-[-12px] xl:top-[-15px]";
</script>

<div
  class="relative flex h-[105px] items-end lg:h-[120px] xl:h-[125px] {containerFocusClass} {componentClass}"
>
  <div class="relative flex w-full items-center border-b-[12px] border-black">
    <label
      for={id}
      class="heading-sm pointer-events-none absolute left-0 top-[9px] bg-transparent text-dark-grey md:top-[6px] xl:top-[2px] {labelOpacityClass} {transitionClass} duration-[150ms]"
    >
      {label}
    </label>
    <div
      class="relative mb-5 flex h-[54px] w-full max-w-[calc(100%-103px)] items-center gap-[10px] md:max-w-[calc(100%-116px)] lg:h-[64px] lg:gap-5 xl:h-[70px] xl:max-w-[calc(100%-132px)]"
    >
      <span
        bind:this={inputFieldDummy}
        aria-hidden="true"
        class="heading-sm invisible absolute bottom-0 left-0 whitespace-pre"
      />
      <input
        type="text"
        name={id}
        {autocomplete}
        bind:this={inputField}
        bind:value={selectedValue}
        on:input={handleInputChange}
        on:input
        on:keyup={(e) => {
          isFocused = true;
          if (e.key === "Tab") {
            isClicked = false;
          }
        }}
        on:click={() => {
          isClicked = true;
          isFocused = true;
        }}
        on:blur={() => (isFocused = false)}
        on:change
        {id}
        {required}
        class="heading-sm h-full w-full max-w-[calc(100%-44px)] bg-transparent outline-none"
      />
      {#if hasValue}
        <div in:fade={{ duration: 150, delay: 150, easing: cubicInOut }}>
          <Button
            on:click={handleClearButtonClick}
            type="button"
            theme="black"
            ariaLabel="Clear search input"
            class="mt-2 flex h-6 w-6 items-center justify-center focus-visible:outline-dashed focus-visible:outline-2 focus-visible:outline-black"
          >
            <Icon iconClass="text-dark-grey" name="close" />
          </Button>
        </div>
      {/if}
    </div>
    <span
      aria-hidden="true"
      class="label pointer-events-none absolute left-0 text-dark-grey {transitionClass} {animatedLabelClass} delay-[150ms] duration-[300ms]"
      >{label}</span
    >
  </div>
</div>
